/**
 * PWA Utilities for TENSION
 * Handles service worker registration and PWA lifecycle
 */

export function registerServiceWorker() {
  // TEMPORARILY DISABLED to fix "Illegal constructor" error in production
  console.log('[PWA] Service worker registration DISABLED');
  return;
  
  /* DISABLED CODE - RE-ENABLE AFTER FIXING NOTIFICATION ISSUE
  // Re-enabled after fixing iOS Notification API crash
  console.log('[PWA] Initializing service worker registration...');
  
  if ('serviceWorker' in navigator) {
    window.addEventListener('load', async () => {
      try {
        console.log('[PWA] Attempting to register service worker...');
        
        const registration = await navigator.serviceWorker.register('/service-worker.js', {
          scope: '/',
        });

        console.log('[PWA] Service Worker registered successfully:', registration.scope);

        // Check for updates periodically
        setInterval(() => {
          registration.update();
        }, 60 * 60 * 1000); // Check every hour

        // Listen for updates
        registration.addEventListener('updatefound', () => {
          const newWorker = registration.installing;
          
          if (newWorker) {
            newWorker.addEventListener('statechange', () => {
              if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                // New service worker available
                console.log('[PWA] New version available');
                
                // Optionally notify user about update
                if (window.confirm('A new version of TENSION is available. Reload to update?')) {
                  newWorker.postMessage({ type: 'SKIP_WAITING' });
                  window.location.reload();
                }
              }
            });
          }
        });

        // Handle controller change (new service worker activated)
        navigator.serviceWorker.addEventListener('controllerchange', () => {
          console.log('[PWA] Controller changed, reloading...');
          window.location.reload();
        });

      } catch (error) {
        console.error('[PWA] Service Worker registration failed:', error);
        console.error('[PWA] Error details:', error.message);
        
        // Continue without service worker - app should still work
        console.log('[PWA] App will continue without service worker (online-only mode)');
      }
    });
  } else {
    console.log('[PWA] Service workers not supported in this browser');
  }
  */

}

export function unregisterServiceWorker() {
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.ready.then((registration) => {
      registration.unregister();
    });
  }
}

export function isStandalone(): boolean {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    (window.navigator as any).standalone === true ||
    document.referrer.includes('android-app://')
  );
}

export function canInstall(): boolean {
  return 'BeforeInstallPromptEvent' in window || isIOS();
}

export function isIOS(): boolean {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
}

export function getInstallInstructions(): string[] {
  if (isIOS()) {
    return [
      'Tap the Share button in Safari',
      'Scroll down and tap "Add to Home Screen"',
      'Tap "Add" in the top right corner',
    ];
  }
  
  // Android/Chrome
  return [
    'Tap the menu button (⋮) in your browser',
    'Tap "Install app" or "Add to Home Screen"',
    'Tap "Install" to confirm',
  ];
}

// Request notification permission
export async function requestNotificationPermission(): Promise<NotificationPermission> {
  if (!('Notification' in window)) {
    console.log('[PWA] Notifications not supported');
    return 'denied';
  }

  // DEFENSIVE: Wrap all Notification API access in try-catch
  try {
    if (Notification.permission === 'granted') {
      return 'granted';
    }

    if (Notification.permission !== 'denied') {
      const permission = await Notification.requestPermission();
      return permission;
    }

    return Notification.permission;
  } catch (error) {
    console.debug('[PWA] Notification API error:', error);
    return 'denied';
  }
}

// Subscribe to push notifications
export async function subscribeToPushNotifications() {
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
    console.log('[PWA] Push notifications not supported');
    return null;
  }

  try {
    const registration = await navigator.serviceWorker.ready;
    
    // Check if already subscribed
    let subscription = await registration.pushManager.getSubscription();
    
    if (!subscription) {
      // Request permission first
      const permission = await requestNotificationPermission();
      
      if (permission !== 'granted') {
        console.log('[PWA] Notification permission denied');
        return null;
      }

      // Subscribe to push notifications
      // Note: You'll need to generate VAPID keys for production
      const vapidKey = urlBase64ToUint8Array(process.env.VITE_VAPID_PUBLIC_KEY || '');
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: vapidKey as BufferSource,
      });
    }

    return subscription;
  } catch (error) {
    console.error('[PWA] Failed to subscribe to push notifications:', error);
    return null;
  }
}

// Helper function for VAPID key conversion
function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  
  return outputArray;
}

// Check if app is online
export function isOnline(): boolean {
  return navigator.onLine;
}

// Listen for online/offline events
export function addConnectivityListeners(
  onOnline: () => void,
  onOffline: () => void
) {
  window.addEventListener('online', onOnline);
  window.addEventListener('offline', onOffline);

  return () => {
    window.removeEventListener('online', onOnline);
    window.removeEventListener('offline', onOffline);
  };
}